import { Link } from 'react-router-dom';
import EmptyState from '@/components/ui/EmptyState';
import type { PriorityListProps, PrioritySeverity } from './types';

const severityClasses: Record<PrioritySeverity, string> = {
  neutral: 'border-gray-300 dark:border-gray-700',
  info: 'border-club-blue-500',
  warning: 'border-warning-500',
  critical: 'border-danger-500',
  success: 'border-success-500',
};

const severityBadges: Record<PrioritySeverity, { label: string; classes: string }> = {
  neutral: { label: 'Update', classes: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200' },
  info: { label: 'Info', classes: 'bg-club-blue-100 text-club-blue-800 dark:bg-club-blue-950 dark:text-club-blue-200' },
  warning: { label: 'Warning', classes: 'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-200' },
  critical: { label: 'Critical', classes: 'bg-danger-100 text-danger-800 dark:bg-danger-900/30 dark:text-danger-200' },
  success: { label: 'Success', classes: 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-200' },
};

export default function PriorityList({ ariaLabel, items, emptyTitle }: PriorityListProps) {
  if (items.length === 0) return <EmptyState title={emptyTitle} className="py-10" />;

  return (
    <ul aria-label={ariaLabel} className="space-y-2">
      {items.map((item) => (
        <li key={item.id} className={`min-w-0 rounded-lg border-l-4 bg-gray-50 p-3 dark:bg-gray-800/60 ${severityClasses[item.severity]}`}>
          <div className="flex min-w-0 flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 w-full sm:flex-1">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <p className="min-w-0 break-words text-sm font-semibold text-gray-900 dark:text-gray-100">{item.title}</p>
                <span className={`rounded-full px-2 py-1 text-xs font-semibold ${severityBadges[item.severity].classes}`}>
                  {severityBadges[item.severity].label}
                </span>
              </div>
              {item.context && <p className="mt-1 break-words text-xs text-gray-600 dark:text-gray-400">{item.context}</p>}
            </div>
            <Link
              to={item.action.to}
              aria-label={`${item.action.label} ${item.title}`}
              className="inline-flex min-h-11 w-full max-w-full items-center justify-center whitespace-normal break-words rounded-md px-3 text-center text-sm font-semibold text-club-blue-700 transition-colors hover:bg-club-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 motion-reduce:transition-none dark:text-club-blue-300 dark:hover:bg-club-blue-950 sm:w-auto"
            >
              {item.action.label}
            </Link>
          </div>
        </li>
      ))}
    </ul>
  );
}
